import type React from "react";

import { Banner, Button } from "@alpacahq/ui/old";

import { Icon } from "~/v2/components/AssetIcon/AssetIcon";
import { transformAddress } from "../../util";
import type { TransferCardControllerReturn } from "../TransferCard.controller";

interface ConfirmWithdrawProps
  extends Pick<
    TransferCardControllerReturn,
    "asset" | "fee" | "withdraw" | "assetCode" | "selectedAssetDisplay"
  > {}

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex w-full items-center justify-between gap-2">
    <span className="font-normal text-gray-700 text-sm">{label}</span>
    <div className="flex min-w-0 items-center justify-end gap-2 font-medium text-gray-950 text-sm">
      {children}
    </div>
  </div>
);

export const ConfirmWithdraw = ({
  fee,
  withdraw,
  assetCode,
  selectedAssetDisplay,
}: ConfirmWithdrawProps) => {
  const { chainLabel, assetLabel, chainIconKey, assetIconKey } =
    selectedAssetDisplay;
  const { selectedAddress, sendingTo, walletOwner, custodian, amountReceived } =
    withdraw;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="m-0 p-0 font-semibold text-base text-gray-950 leading-[22px]">
        Confirm Withdrawal
      </h2>

      <div className="flex w-full flex-col gap-1">
        <Row label="Chain">
          <Icon name={chainIconKey} className="h-5 w-5" />
          <span>{chainLabel}</span>
        </Row>
        <Row label="Asset">
          <Icon name={assetIconKey} className="h-5 w-5" />
          <span>{assetLabel}</span>
        </Row>
        <Row label="Address">
          <span className="font-mono">
            {transformAddress(selectedAddress, true)}
          </span>
        </Row>
        {sendingTo && <Row label="Sending to">{sendingTo}</Row>}
        {walletOwner && <Row label="Wallet Owner">{walletOwner}</Row>}
        {custodian && <Row label="Custodian or Exchange">{custodian}</Row>}
      </div>

      <div className="w-full border-gray-200 border-t" />

      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full items-center justify-between gap-2">
          <span className="font-normal text-gray-700 text-sm">
            Est. Network Fee
          </span>
          <span className="font-medium font-mono text-gray-800 text-sm">
            {fee}
            {assetCode ? ` ${assetCode}` : ""}
          </span>
        </div>
        <div className="flex w-full items-center justify-between gap-2">
          <span className="font-semibold text-base text-gray-950 leading-[22px]">
            Amount to Receive
          </span>
          <span className="font-medium font-mono text-base text-gray-950 leading-[22px]">
            {amountReceived}
            {assetCode ? ` ${assetCode}` : ""}
          </span>
        </div>
      </div>

      <Banner
        variant="warning"
        icon={<span />}
        message="Withdrawal is irreversible once submitted."
        className="border border-orange-600 bg-[rgba(254,215,170,0.24)] p-3"
      />

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="v2/stroke"
          size="v2/base"
          className="w-full border-gray-400"
          onClick={withdraw.onEdit}
          disabled={withdraw.isSubmitLoading}
        >
          Edit
        </Button>
        <Button
          variant="v2/filled"
          size="v2/base"
          className="w-full"
          onClick={withdraw.onConfirm}
          loading={withdraw.isSubmitLoading}
          disabled={withdraw.isSubmitDisabled}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
